import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bed, Bath, Car, Maximize2, Layers, Ruler, Compass, ChevronRight, Lock, FileText, Heart } from 'lucide-react';
import { useMarketingAuth } from '../../hooks/useMarketingAuth';
import { useFavourites } from '../../context/FavouritesContext';
import CompareToggle from '../compare/CompareToggle';
import PhotoComingSoon from './PhotoComingSoon';

/**
 * PackageCard, listing card for the Home & Land catalog.
 *
 * Constraint reminder: NEVER renders price, estate name, builder, or
 * design/facade name (those identify the builder and stay locked in the App).
 * Address IS public as of 2026-02-17 - surfaced as a subline so the card
 * gives consumers a real-world reference point.
 *
 * To make the card enticing without leaking sensitive info, we surface:
 *   - Hover image swap (uses second marketing_images entry if present)
 *   - "Just listed" badge for fresh listings (<14 days since created_at)
 *   - "+N inclusions" badge on the image
 *   - Storey + frontage chips
 *   - 3 parsed inclusion highlights (generic, builder-agnostic)
 *   - Floor plan available marker
 */

// Parse the inclusions array into a small set of high-signal, generic chips.
// Tuple = [keyword regex, friendly label, priority (lower = higher)]. First match wins per label.
const INCLUSION_RULES = [
  [/duct/i,                          'Ducted A/C',         1],
  [/(split system|split-system)/i,   'Reverse-cycle A/C',  2],
  [/(stone|caesar|engineered).*bench/i, 'Stone benchtops', 3],
  [/(2700mm|2\.7m|2700 mm)/i,        '2.7m ceilings',      4],
  [/structural\s*guarantee|structural\s*warranty/i, '25-yr structural', 5],
  [/(led\s*downlight|downlight)/i,   'LED downlights',     6],
  [/landscap/i,                       'Landscaping incl.',  7],
  [/(roller door|automatic.*garage|auto.*garage|sectional)/i, 'Auto garage',  8],
  [/(rainwater|water tank)/i,        'Rainwater tank',     9],
  [/(fenc)/i,                        'Fencing incl.',      10],
  [/(driveway|paths)/i,              'Driveway + paths',   11],
  [/(blinds|window furnishings)/i,   'Blinds incl.',       12],
  [/(walk[- ]in\s*robe|wir)/i,       'Walk-in robe',       13],
  [/(dishwasher)/i,                  'Dishwasher',         14],
  [/(oven|cooktop|stainless)/i,      'Stainless appliances', 15],
];

const extractHighlights = (inclusions, max = 3) => {
  if (!Array.isArray(inclusions) || inclusions.length === 0) return [];
  const text = inclusions.join(' \n ');
  const matched = [];
  for (const [re, label] of INCLUSION_RULES) {
    if (matched.length >= max) break;
    if (re.test(text) && !matched.includes(label)) matched.push(label);
  }
  return matched;
};

const isFreshListing = (createdAt) => {
  if (!createdAt) return false;
  const ts = Date.parse(createdAt);
  if (Number.isNaN(ts)) return false;
  const fourteenDays = 14 * 24 * 60 * 60 * 1000;
  return Date.now() - ts < fourteenDays;
};

const PackageCard = ({ listing }) => {
  const [imgIdx, setImgIdx] = useState(0);
  const { isLoggedIn } = useMarketingAuth();
  const { toggleFavourite, isFavourite, openSidebarOn } = useFavourites();
  const faved = isFavourite(listing.listing_slug || listing.slug || listing.id, 'hl_package');
  const images = (listing.marketing_images && listing.marketing_images.length > 0)
    ? listing.marketing_images
    : (listing.images || []);
  const hasMultiple = images.length > 1;
  // Card images are thumbnails - ask the plan-asset proxy for a right-sized
  // width so we never pull a multi-MB 3200px original into a ~340px card.
  const sizeThumb = (u) => (typeof u === 'string' && u.startsWith('/api/plan-asset') && !u.includes('&w=') ? `${u}&w=800` : u);
  const heroImage = sizeThumb(images[imgIdx] || null);

  const hubBase = (listing.state || '').toUpperCase() === 'VIC'
    ? '/house-and-land-melbourne'
    : '/house-and-land-adelaide';
  const detailUrl = `${hubBase}/${listing.suburb_slug}/${listing.listing_slug}`;
  const inclusionCount = Array.isArray(listing.inclusions) ? listing.inclusions.length : 0;
  const highlights = extractHighlights(listing.inclusions);
  const fresh = isFreshListing(listing.created_at);
  const onHold = listing.status && listing.status !== 'Available';
  const storeyLabel = listing.is_double_storey ? 'Double storey' : 'Single storey';
  const hasFloorPlan = (Array.isArray(listing.floor_plan_images) && listing.floor_plan_images.length > 0)
    || Boolean(listing.has_floor_plan);

  // iOS-parity reveal, when the listing has `price_aud` (i.e. it came
  // from the authenticated `/api/packages/full` endpoint), show the
  // real total. Otherwise the card stays as a teaser.
  const fullPrice = typeof listing.price_aud === 'number' && listing.price_aud > 0
    ? listing.price_aud
    : null;
  const formattedPrice = fullPrice
    ? `$${(fullPrice / 1000).toFixed(0)}k`
    : null;
  // Weekly repayment estimate: floored to nearest $25 by the backend so
  // anonymous users can see a "from $X/wk" indicator without leaking
  // exact price. Same formula as the Equable Finance Calculator
  // (6.0% p.a. · 30 yr · 80% LVR).
  const weeklyEstimate = typeof listing.weekly_repayment_estimate === 'number' && listing.weekly_repayment_estimate > 0
    ? listing.weekly_repayment_estimate
    : null;
  const builderName = listing.builder_name || null;

  return (
    <Link
      to={detailUrl}
      className="group block bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-[0_18px_38px_rgba(0,0,0,0.12)] hover:border-[#1d1d1f]/40 hover:-translate-y-0.5 transition-all duration-300"
      data-testid={`package-card-${listing.id}`}
      onMouseEnter={() => hasMultiple && setImgIdx(1)}
      onMouseLeave={() => hasMultiple && setImgIdx(0)}
    >
      <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
        {heroImage ? (
          <img
            src={heroImage}
            alt={`Home and land package in ${listing.suburb}`}
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <PhotoComingSoon state={listing.state} />
        )}

        {/* Subtle bottom gradient so badges remain readable on any photo */}
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/55 to-transparent pointer-events-none" />

        {/* Top-left: suburb pill */}
        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-[11px] font-semibold text-[#1d1d1f]">
          {listing.suburb}
        </div>

        {/* Top-right: status / freshness badges */}
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
          {onHold && (
            <span className="bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full">
              {listing.status === 'OnHold' ? 'On hold' : listing.status}
            </span>
          )}
          {!onHold && fresh && (
            <span className="bg-[#febd59] text-[#1d1d1f] text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full inline-flex items-center gap-1">
              <Compass className="w-3 h-3" /> Just listed
            </span>
          )}
        </div>

        {/* Bottom overlay - single wrapping row so badges/buttons never overlap */}
        <div className="absolute inset-x-3 bottom-3 flex flex-wrap items-center gap-1.5">
          {hasMultiple && (
            <span className="bg-black/55 backdrop-blur-sm text-white text-[10.5px] font-semibold px-2 py-0.5 rounded-full">
              {imgIdx + 1} / {images.length}
            </span>
          )}
          {inclusionCount > 0 && (
            <span className="bg-white/95 backdrop-blur-sm text-[#1d1d1f] text-[10.5px] font-semibold px-2 py-0.5 rounded-full">
              + {inclusionCount} inclusions
            </span>
          )}
          {hasFloorPlan && (
            <span className="inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm text-[#1d1d1f] text-[10.5px] font-semibold px-2 py-0.5 rounded-full">
              <FileText className="w-3 h-3" /> Floor plan
            </span>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            <CompareToggle
              item={{
                __label: listing.address || `${listing.bedrooms || ''} bed in ${listing.suburb || ''}`.trim(),
                __href: detailUrl,
                __sub: listing.suburb ? `${listing.suburb}${listing.state ? `, ${listing.state}` : ''}` : '',
                bedrooms: listing.bedrooms,
                bathrooms: listing.bathrooms,
                garages: listing.garages,
                floor_area: listing.floor_area,
                area_m2: listing.area_m2,
                is_double_storey: listing.is_double_storey,
                frontage_m: listing.frontage_m,
                inclusions: listing.inclusions,
                suburb: listing.suburb,
                state: listing.state,
              }}
              kind="hl_package"
              id={listing.id}
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleFavourite(listing, 'hl_package');
                if (!faved) setTimeout(() => openSidebarOn('hl_package'), 250);
              }}
              aria-label={faved ? 'Remove from shortlist' : 'Add to shortlist'}
              data-testid={`package-card-shortlist-${listing.id}`}
              className={`inline-flex items-center justify-center w-8 h-8 rounded-full backdrop-blur-sm transition-all ${
                faved ? 'bg-red-500 text-white' : 'bg-white/95 text-[#1d1d1f] hover:bg-white'
              }`}
            >
              <Heart className={`w-4 h-4 ${faved ? 'fill-current' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4">
        <h3 className="text-[15px] font-semibold text-[#1d1d1f] leading-tight mb-1 group-hover:text-[#1d8efe] transition-colors line-clamp-2" title={listing.address || ''} data-testid={`package-card-title-${listing.id}`}>
          {listing.address || (
            <>
              {listing.bedrooms ? `${listing.bedrooms} bed` : 'Home'}
              {listing.bathrooms ? ` · ${listing.bathrooms} bath` : ''}
              {listing.garages ? ` · ${listing.garages} car` : ''} in {listing.suburb}
            </>
          )}
        </h3>
        <p
          className="text-[12px] text-[#5a5a5f] mb-1 truncate hidden sm:block"
          data-testid={`package-card-subline-${listing.id}`}
        >
          {listing.bedrooms ? `${listing.bedrooms} bed` : 'Home'}
          {listing.bathrooms ? ` · ${listing.bathrooms} bath` : ''}
          {listing.garages ? ` · ${listing.garages} car` : ''}
          {listing.suburb ? ` · ${listing.suburb}${listing.state ? `, ${listing.state}` : ''}` : ''}
        </p>
        <p
          className="text-[10.5px] italic text-[#86868b] mb-2.5 hidden sm:block"
          data-testid={`package-card-two-contracts-${listing.id}`}
        >
          Land usually sold separately by the listing agent
        </p>

        {/* Storey + frontage micro-chips */}
        <div className="hidden sm:flex flex-wrap items-center gap-1.5 mb-3">
          <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-[#1d1d1f] bg-[#f5f5f7] border border-gray-200 px-2 py-0.5 rounded-full">
            <Layers className="w-3 h-3" /> {storeyLabel}
          </span>
          {listing.frontage_m && (
            <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-[#1d1d1f] bg-[#f5f5f7] border border-gray-200 px-2 py-0.5 rounded-full">
              <Ruler className="w-3 h-3" /> {Math.round(listing.frontage_m)}m frontage
            </span>
          )}
        </div>

        {/* Compact specs row */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12.5px] text-[#86868b] mb-2 sm:mb-3 tabular-nums">
          {listing.bedrooms != null && (
            <span className="inline-flex items-center gap-1">
              <Bed className="w-3.5 h-3.5" /> {listing.bedrooms}
            </span>
          )}
          {listing.bathrooms != null && (
            <span className="inline-flex items-center gap-1">
              <Bath className="w-3.5 h-3.5" /> {listing.bathrooms}
            </span>
          )}
          {listing.garages != null && (
            <span className="inline-flex items-center gap-1">
              <Car className="w-3.5 h-3.5" /> {listing.garages}
            </span>
          )}
          {listing.floor_area && (
            <span className="inline-flex items-center gap-1">
              <Maximize2 className="w-3.5 h-3.5" /> {Math.round(parseFloat(listing.floor_area))}m² home
            </span>
          )}
        </div>

        {/* Inclusion highlights, parsed from the feed (generic, no builder leak) */}
        {highlights.length > 0 && (
          <div className="hidden sm:flex flex-wrap gap-1 mb-2 sm:mb-3" data-testid={`package-card-highlights-${listing.id}`}>
            {highlights.map((h) => (
              <span
                key={h}
                className="text-[10.5px] font-medium text-[#1d8efe] bg-[#1d8efe]/8 border border-[#1d8efe]/15 px-2 py-0.5 rounded-full"
              >
                {h}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-gray-100 tabular-nums">
          {isLoggedIn ? (
            <>
              <span className="text-[13px] text-[#1d1d1f]">
                {formattedPrice ? (
                  <>
                    <span className="font-bold text-[15px]">From ~{formattedPrice}</span>
                    <span className="block text-[10.5px] font-normal text-[#86868b] not-italic">indicative total</span>
                  </>
                ) : (
                  <span className="text-[#86868b]">
                    {listing.area_m2 ? `${Math.round(listing.area_m2)}m² land` : 'Land package'}
                  </span>
                )}
                {weeklyEstimate && (
                  <span className="block text-[11.5px] font-semibold text-[#10b981] mt-0.5" data-testid={`package-card-weekly-${listing.id}`}>
                    ~${weeklyEstimate}/wk*
                  </span>
                )}
                {builderName && (
                  <span className="block text-[11.5px] text-[#86868b] mt-0.5 truncate max-w-[180px]">
                    by {builderName}
                  </span>
                )}
              </span>
              <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#1d8efe] group-hover:text-[#1d1d1f] transition-colors">
                View details <ChevronRight className="w-3 h-3" />
              </span>
            </>
          ) : (
            <>
              <span className="text-[12.5px] text-[#86868b]">
                {weeklyEstimate ? (
                  <span className="block text-[13px] font-bold text-[#10b981]" data-testid={`package-card-weekly-${listing.id}`}>
                    From ~${weeklyEstimate}/wk*
                  </span>
                ) : (
                  listing.area_m2 ? `${Math.round(listing.area_m2)}m² land` : 'Land package'
                )}
              </span>
              <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#1d8efe] group-hover:text-[#1d1d1f] transition-colors">
                <Lock className="w-3 h-3" /> Unlock price + plan
              </span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
};

export default PackageCard;
